import { __decorate } from "tslib";
import React from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { action, observable, runInAction } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import Trigger from 'choerodon-ui/lib/trigger/Trigger';
import { TextField } from '../text-field/TextField';
import autobind from '../_util/autobind';
import Icon from '../icon';
import TaskRunner from '../_util/TaskRunner';
import BUILT_IN_PLACEMENTS from './placements';
export default class TriggerField extends TextField {
    constructor(props, context) {
        super(props, context);
        this.popupTask = new TaskRunner();
        runInAction(() => {
            this.statePopup = false;
        });
    }
    get popup() {
        return this.statePopup;
    }
    saveTrigger(node) {
        this.trigger = node;
    }
    isValidationMessageHidden(message) {
        return super.isValidationMessageHidden(message) || this.popup;
    }
    setPopup(statePopup) {
        if (statePopup !== this.statePopup) {
            this.statePopup = statePopup;
            const { onPopupHiddenChange = noop } = this.props;
            onPopupHiddenChange(!statePopup);
        }
    }
    getRootDomNode() {
        return findDOMNode(this);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'popupContent',
            'popupCls',
            'popupStyle',
            'popupPlacement',
            'editable',
            'trigger',
            'triggerShowDelay',
            'triggerHiddenDelay',
            'onPopupHiddenChange',
            'getPopupContainer',
            'getPopupAlignTarget',
            'extraFooterPlacement',
            'renderExtraFooter',
            'treeDefaultExpandedKeys',
            'treeDefaultExpandAll',
            'tabIntoPopupContent',
            'viewMode',
        ]);
    }
    getPopupProps() {
        return {
            setValue: this.setValue.bind(this),
            setPopup: this.setPopup.bind(this),
        };
    }
    getPopupClassName(defaultClassName) {
        return defaultClassName;
    }
    renderPopupContent() {
        const { popupContent } = this.props;
        if (popupContent === undefined) {
            return this.getPopupContent();
        }
        if (popupContent instanceof Function) {
            return popupContent(this.getPopupProps());
        }
        return popupContent;
    }
    getWrappedEditor(renderedValue) {
        const { prefixCls, props: { popupCls, popupStyle, popupPlacement, hidden, trigger, triggerShowDelay, triggerHiddenDelay, getPopupContainer, tabIntoPopupContent, getPopupAlignTarget = this.getRootDomNode, }, } = this;
        return (React.createElement(Trigger, { ref: this.saveTrigger, action: this.readOnly || this.disabled ? [] : trigger, focusDelay: triggerShowDelay, blurDelay: triggerHiddenDelay, mouseEnterDelay: triggerShowDelay, mouseLeaveDelay: triggerHiddenDelay, prefixCls: prefixCls, popupCls: this.getPopupClassName(popupCls), popupStyle: popupStyle, popupContent: this.renderPopupContent, popupPlacement: popupPlacement, popupHidden: hidden || !this.popup, builtinPlacements: BUILT_IN_PLACEMENTS, onPopupAnimateAppear: this.handlePopupAnimateAppear, onPopupAnimateEnd: this.handlePopupAnimateEnd, onPopupHiddenChange: this.handlePopupHiddenChange, getPopupStyleFromAlign: this.getPopupStyleFromAlign, getRootDomNode: getPopupAlignTarget, getPopupContainer: getPopupContainer, tabIntoPopupContent: tabIntoPopupContent, childrenProps: renderedValue }, this.getEditor));
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        return super.getWrapperClassNames(...args, {
            [`${prefixCls}-expand`]: this.popup,
            [`${prefixCls}-not-editable`]: !this.disabled && !this.editable,
        });
    }
    getDefaultSuffix() {
        const { prefixCls } = this;
        return React.createElement(Icon, { type: this.getTriggerIconFont(), className: `${prefixCls}-trigger` });
    }
    handleTagAnimateEnd() {
        this.forcePopupAlign();
    }
    handlePopupMouseDown(e) {
        e.preventDefault();
        const { onMouseDown = noop } = this.props;
        onMouseDown(e);
    }
    handlePopupHiddenChange(hidden) {
        this.setPopup(!hidden);
    }
    forcePositionChanged() {
        this.forcePopupAlign();
    }
    forcePopupAlign() {
        if (this.trigger) {
            this.trigger.forcePopupAlign();
        }
    }
    expand() {
        this.popupTask.cancel();
        if (!this.readOnly && !this.popup) {
            this.popupTask.delay(this.props.triggerShowDelay, () => {
                this.setPopup(true);
            });
        }
    }
    collapse() {
        this.popupTask.cancel();
        if (!this.readOnly && this.popup) {
            const { element } = this;
            // 如果当前焦点在popup中，将焦点换给输入框
            let triggerHiddenDelay = this.props.triggerHiddenDelay;
            if (this.isFocused && element !== document.activeElement) {
                element.focus();
                const { triggerShowDelay } = this.props;
                triggerHiddenDelay += triggerShowDelay;
            }
            this.popupTask.delay(triggerHiddenDelay, () => {
                this.setPopup(false);
            });
        }
    }
}
TriggerField.displayName = 'TriggerField';
TriggerField.propTypes = {
    /**
     * 下拉框的自定义内容
     */
    popupContent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 下拉框的自定义样式名
     */
    popupCls: PropTypes.string,
    /**
     * 下拉框的内链样式
     */
    popupStyle: PropTypes.object,
    /**
     * 下拉框对齐方式
     */
    popupPlacement: PropTypes.string,
    /**
     * 触发下拉框的方式
     * 可选值：click | focus | hover | contextMenu
     */
    trigger: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.string),
    /**
     * 下拉框显示延迟
     * @defualt 150
     */
    triggerShowDelay: PropTypes.number,
    /**
     * 下拉框隐藏延迟
     * @defualt 50
     */
    triggerHiddenDelay: PropTypes.number,
    /**
     * 下拉框变化钩子
     */
    onPopupHiddenChange: PropTypes.func,
    /**
     * 定义浮层的容器，默认为 body
     */
    getPopupContainer: PropTypes.func,
    /**
     * 定义浮层对齐的目标，默认为组件最外层元素
     */
    getPopupAlignTarget: PropTypes.func,
    /**
     * 当popup中有可获取焦点对象时，是否按tab键时获取焦点
     */
    tabIntoPopupContent: PropTypes.bool,
    viewMode: PropTypes.oneOf(['none', 'popup', 'modal', 'list']),
    ...TextField.propTypes,
};
TriggerField.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'trigger',
    clearButton: true,
    popupPlacement: 'bottomLeft',
    trigger: ['focus', 'click'],
    triggerShowDelay: 150,
    triggerHiddenDelay: 50,
    viewMode: 'popup',
};
__decorate([
    observable
], TriggerField.prototype, "statePopup", void 0);
__decorate([
    autobind
], TriggerField.prototype, "saveTrigger", null);
__decorate([
    autobind
], TriggerField.prototype, "isValidationMessageHidden", null);
__decorate([
    action
], TriggerField.prototype, "setPopup", null);
__decorate([
    autobind
], TriggerField.prototype, "getRootDomNode", null);
__decorate([
    autobind
], TriggerField.prototype, "renderPopupContent", null);
__decorate([
    autobind
], TriggerField.prototype, "handleTagAnimateEnd", null);
__decorate([
    autobind
], TriggerField.prototype, "handlePopupMouseDown", null);
__decorate([
    autobind
], TriggerField.prototype, "handlePopupHiddenChange", null);
